'use client';

import { useEffect, useMemo } from 'react';
import { useI18n } from 'next-localization';

import { pick } from 'lodash-es';

import { useDistanceContext } from '@/hooks/use-distance-context';
import { cn, isPhysicianSiteType } from '@/lib/utils';
import { useOasApp } from '@/providers/oas-app-provider';
import { NeuFeedback, NeuPagination } from '@hcahealthcaredpx/neutron-react';

import {
  DEFAULT_LANGUAGE,
  DEFAULT_REQ_PAYLOAD,
  DEFAULT_SORT_BY,
  DISTANCE_SORT_BY,
  ITEMS_PER_PAGE,
} from '../constants';
import { useDoctorsListing, useListingContext } from '../hooks';
import styles from '../listing.module.css';
import { ClearButtonRef } from '../listing.types';
import { scrollDoctorListingInView } from '../utils';
import { DoctorCard } from './doctor-card';
import { ListHeader } from './list-header';
import { SearchTips } from './search-tips';

type ListProps = {
  clearButtonRef: ClearButtonRef;
};

export const List = ({ clearButtonRef }: ListProps): JSX.Element => {
  const { t } = useI18n();
  const fields = useOasApp();
  const {
    coids,
    program,
    siteType,
    isLocation,
    locationUrlTitle: currentLocationUrlTitle,
  } = fields;
  const isPhysicians = isPhysicianSiteType(siteType);

  const { appliedFilters, setAppliedFilters, setLanguages, setFacilities } = useListingContext();
  const { location } = useDistanceContext();
  const { zip, longitude, latitude } = location || { zip: '', longitude: null, latitude: null };

  const pickedFilters = pick(appliedFilters, [
    'gender',
    'languages',
    'teleMedicineFlg',
    'showAcceptingNewPatientsOnly',
    'zip',
    'latitude',
    'longitude',
    'sortType',
    'facilityCoid',
    'specialtyId',
    'keywords',
    'keywordExact',
    'locationUrlTitle',
  ]);

  const hasDistanceParamsApplied = !!(latitude && longitude);

  const addressParamData = { zip, latitude, longitude };

  const pickedFacilityValuePair = pickedFilters?.facilityCoid
    ? pickedFilters?.facilityCoid.split('|')
    : undefined;
  const pickedFacilityCoid =
    pickedFacilityValuePair && pickedFacilityValuePair?.length > 0
      ? pickedFacilityValuePair[0]
      : undefined;
  const pickedFacilityLocationUrlTitle =
    pickedFacilityValuePair && pickedFacilityValuePair.length > 1
      ? pickedFacilityValuePair[1]
      : undefined;

  const searchOneParams = useMemo(() => {
    return {
      specialtyId: pickedFilters?.specialtyId,
      keywordExact: pickedFilters?.keywordExact,
      locationUrlTitle:
        pickedFilters?.locationUrlTitle ||
        pickedFacilityLocationUrlTitle ||
        (isLocation && !pickedFilters?.locationUrlTitle
          ? currentLocationUrlTitle
          : !pickedFilters?.keywordExact && !pickedFilters?.specialtyId
            ? appliedFilters.locationUrlTitle
            : undefined),
    };
  }, [
    pickedFilters,
    appliedFilters,
    pickedFacilityLocationUrlTitle,
    isLocation,
    currentLocationUrlTitle,
  ]);

  const currentPage = appliedFilters?.meta?.page || 1;

  const { data, isFetching, isSuccess } = useDoctorsListing({
    ...DEFAULT_REQ_PAYLOAD,
    ...pickedFilters,
    coids: coids,
    program,
    siteType,
    randomize:
      !isPhysicians &&
      !hasDistanceParamsApplied &&
      (appliedFilters?.sortType === null || appliedFilters?.sortType === DEFAULT_SORT_BY),
    meta: {
      page: currentPage,
      pageSize: ITEMS_PER_PAGE,
    },
    facilityCoid: pickedFacilityCoid,
    specialtyId: searchOneParams.specialtyId,
    keywordExact: searchOneParams.keywordExact,
    locationUrlTitle: searchOneParams.locationUrlTitle,
    zip: pickedFilters.zip ? pickedFilters.zip : addressParamData.zip,
    latitude: pickedFilters.latitude ? pickedFilters.latitude : addressParamData.latitude,
    longitude: pickedFilters.longitude ? pickedFilters.longitude : addressParamData.longitude,
    sortType:
      hasDistanceParamsApplied && !appliedFilters.sortType
        ? DISTANCE_SORT_BY
        : pickedFilters?.sortType,
  });

  useEffect(() => {
    if (isSuccess) {
      setFacilities((prevState) => {
        // Todo: this can be removed if the API always return all facilities
        if (prevState?.length > 1) {
          return prevState;
        }
        return data?.data?.facilityMap;
      });
      setLanguages(() => {
        return [
          DEFAULT_LANGUAGE,
          ...(data?.data?.filteredLanguages || [])
            .filter((lang) => lang !== DEFAULT_LANGUAGE)
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
        ];
      });
    }
  }, [
    data?.data?.facilityMap,
    data?.data?.filteredLanguages,
    isSuccess,
    setFacilities,
    setLanguages,
  ]);

  const changePaginationHandler = (value: number) => {
    setAppliedFilters((prev) => ({
      ...prev,
      meta: {
        page: value,
        pageSize: ITEMS_PER_PAGE,
      },
    }));

    // Scroll to top of listing after pagination change
    scrollDoctorListingInView();
  };

  const isLocationWithDistanceFound = useMemo(
    () => data?.data?.providerList?.some((p) => p.providerLocations?.some((loc) => loc.distance)),
    [data?.data?.providerList]
  );
  const hasDistance = hasDistanceParamsApplied || isLocationWithDistanceFound || false;
  const totalProvidersCount = data?.data?.totalProvidersCount || 0;
  const hasNoResults = totalProvidersCount === 0;
  const endResults = currentPage * ITEMS_PER_PAGE;

  return (
    <div className="col-sm-12 col-md-12 col-lg-8 py-0 px-3 overflow-hidden">
      <div id="doctors-listing">
        <ListHeader
          totalProvidersCount={totalProvidersCount}
          hasDistance={hasDistance}
          startResults={currentPage * ITEMS_PER_PAGE - (ITEMS_PER_PAGE - 1)}
          endResults={endResults > totalProvidersCount ? totalProvidersCount : endResults}
          totalResults={totalProvidersCount}
          isLoading={isFetching}
          isPhysicians={isPhysicians}
        />
        {isFetching ? (
          <NeuFeedback
            kind="spinner"
            label={t('OAS.Listing.LoadingText')}
            text={t('OAS.Listing.LoadingDescription')}
          />
        ) : (
          <>
            {hasNoResults ? (
              <div className="neu-bg-yellow-0 neu-text-yellow-70 p-5 m-5">
                <h5 className="text-center text-bold">{t('OAS.Listing.NoDoctors')}</h5>
                <p className="text-center">
                  {t('OAS.Listing.PleaseRemoveOr')}{' '}
                  <a href="#" onClick={() => clearButtonRef.current?.clear()}>
                    {t('OAS.Listing.ResetFilters')}
                  </a>{' '}
                  {t('OAS.Listing.ToSeeMore')}
                </p>
                <SearchTips />
              </div>
            ) : (
              <ul className="m-0 p-0">
                {data?.data?.providerList?.map((physician, index) => {
                  return (
                    <li
                      key={`${physician.physicianNpi}-${+index}`}
                      className={cn('py-3', styles.listDoctorCardLi)}
                    >
                      <DoctorCard
                        physician={physician}
                        physicianIndex={index + 1}
                        totalCount={totalProvidersCount}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
        <div className="d-flex justify-content-center">
          <NeuPagination
            kind="paginator"
            current={currentPage}
            results={totalProvidersCount}
            limit={ITEMS_PER_PAGE}
            onNeuChange={(e) => changePaginationHandler(e.value as number)}
            className="mt-5"
          />
        </div>
      </div>
    </div>
  );
};
